import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";

import { onlyNumberKeys } from "utils";

const RefugeAreaDetails = ({ formik, t }) => {
  const {
    values: {
      lifeSafety: { refugeAreas },
      fireSafetyAddress: { floors },
    },
    setFieldValue,
    setFieldTouched,
  } = formik;

  const handleAddNewRefugeArea = (refugeAreas) => {
    setFieldValue("lifeSafety.refugeAreas", [
      ...refugeAreas,
      {
        id: 0,
        floors: [],
        area: "",
      },
    ]);

    setFieldTouched(
      `lifeSafety.refugeAreas[${refugeAreas.length}]`,
      false,
      false
    );
  };

  const handleRemoveRefugeArea = (index) => {
    setFieldValue(
      "lifeSafety.refugeAreas",
      refugeAreas.filter((_, ind) => ind !== index)
    );
  };

  // creating initial values for refuge areas
  useEffect(() => {
    if (!refugeAreas.length && floors.length) {
      setFieldValue("lifeSafety.refugeAreas", [
        {
          id: 0,
          area: "",
          floors: [],
        },
      ]);
    }
  }, [floors]);

  return (
    <>
      <Grid item xs={12} className="mt-3">
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Box>
            <Typography className="mb-3 me-3" lineHeight={1} variant="h6">
              {t("Refuge Areas")}
            </Typography>
          </Box>
          <Box>
            <Button
              color="primary"
              className="mb-3"
              sx={{ minWidth: "unset", textTransform: "capitalize" }}
              variant="outlined"
              onClick={() => handleAddNewRefugeArea(refugeAreas)}
            >
              <strong>
                <span>+</span> {t("Add Refuge Area")}
              </strong>
            </Button>
          </Box>
        </Box>
      </Grid>
      {!!refugeAreas.length &&
        refugeAreas.map(({ floors: refugeFloors, area }, index) => (
          <Grid
            key={index}
            container
            columnSpacing={1}
            rowSpacing={1}
            sx={{ pl: 1 }}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                label={t("Floors")}
                name={`lifeSafety.refugeAreas.${index}.floors`}
                formik={formik}
                options={floors}
                multiple
                disableCloseOnSelect
                isOptionEqualToValue={(option, { value }) =>
                  option.value === value
                }
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props} key={option.value}>
                      <Checkbox
                        sx={{ marginRight: "8px" }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  );
                }}
                onChange={(event, options) => {
                  if (event.key !== "Backspace" && event.key !== "Delete") {
                    setFieldValue(
                      `lifeSafety.refugeAreas.${index}.floors`,
                      options
                    );
                  }
                }}
                value={refugeFloors || []}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("Area") + "*"}
                name={`lifeSafety.refugeAreas.${index}.area`}
                type="number"
                formik={formik}
                placeholder="Enter Refuge Area"
                value={area ?? ""}
                disabled={!refugeFloors.length}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Sq. Mtrs</InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
            {refugeAreas.length > 1 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Button
                  color="error"
                  size="small"
                  sx={{
                    minWidth: "unset",
                    border: "1px solid",
                    height: "40px",
                    width: "100px",
                    mb: 2,
                  }}
                  onClick={() => handleRemoveRefugeArea(index)}
                >
                  <strong className="d-flex justify-content-center">
                    <DeleteOutlineSharpIcon fontSize="small" />
                    {t("Remove")}
                  </strong>
                </Button>
              </Grid>
            )}
          </Grid>
        ))}
    </>
  );
};

RefugeAreaDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(RefugeAreaDetails);
